import { StAgent, StAgentDefaultProfile, StAgentEquityPoint, StI18nEntry } from '../../../services/socialTradingService';
import { emptyLangPack, packFromEntries } from '../i18nLang';

export const REQUIRED_REASONING_TITLES = [
  'PatternBreakout',
  'MacroEnvironment',
  'Confidence',
  'RiskReview',
] as const;

export const REASONING_TITLE_CN: Record<(typeof REQUIRED_REASONING_TITLES)[number], string> = {
  PatternBreakout: '形态突破',
  MacroEnvironment: '宏观环境',
  Confidence: '置信度',
  RiskReview: '风控复核',
};

export type ReasoningFormEntry = {
  title: string;
  detailCn: string;
  detailEn: string;
};

export type AgentForm = {
  name: string;
  strategyId: string;
  strategyName: string;
  description: string;
  avatar: string;
  isActive: boolean;
  symbol: string;
  ohlcv: string;
  version: string;
  riskLevel: string;
  productTags: string;
  winRate: string;
  sharpe: string;
  maxDrawdown: string;
  pnl30d: string;
  sevenDayReturn: string;
  fromBeginning: string;
  restoreDefaultProfile: boolean;
  strategyNameLang: StI18nEntry[];
  descriptionLang: StI18nEntry[];
  strategyNameLangDirty: boolean;
  descriptionLangDirty: boolean;
  i18nStale: boolean;
};

export type CardForm = {
  strategyId: string;
  agentId: string;
  title: string;
  symbol: string;
  ohlcv: string;
  side: 'long' | 'short';
  entry: string;
  stopLoss: string;
  takeProfit: string;
  leverage: string;
  confidence: string;
  version: string;
  riskLevel: string;
  productTags: string;
  winRate: string;
  sharpe: string;
  maxDrawdown: string;
  pnl30d: string;
  sevenDayReturn: string;
  fromBeginning: string;
  reasoning: ReasoningFormEntry[];
  cardValidHours: string;
  source: string;
};

export function defaultsOf(a: StAgent): StAgentDefaultProfile {
  return a.defaultProfile || {};
}

export function defaultReasoningForm(): ReasoningFormEntry[] {
  return REQUIRED_REASONING_TITLES.map((title) => ({
    title,
    detailCn: '',
    detailEn: '',
  }));
}

/** Merge existing agent reasoning into the fixed 4-title form (extras kept after). */
export function reasoningFormFromEntries(
  entries?: { title?: string; detailCn?: string; detailEn?: string }[] | null,
): ReasoningFormEntry[] {
  const byTitle = new Map<string, ReasoningFormEntry>();
  (entries || []).forEach((e) => {
    const title = (e.title || '').trim();
    if (!title) return;
    byTitle.set(title, {
      title,
      detailCn: (e.detailCn || '').trim(),
      detailEn: (e.detailEn || '').trim(),
    });
  });
  const required: ReasoningFormEntry[] = REQUIRED_REASONING_TITLES.map(
    (title) => byTitle.get(title) || { title, detailCn: '', detailEn: '' },
  );
  const extras = Array.from(byTitle.values()).filter(
    (e) => !(REQUIRED_REASONING_TITLES as readonly string[]).includes(e.title),
  );
  return [...required, ...extras];
}

export function validateReasoningForm(entries: ReasoningFormEntry[]): string | null {
  const missing = REQUIRED_REASONING_TITLES.find((req) => {
    const e = entries.find((x) => x.title === req);
    return !e || !e.detailCn.trim() || !e.detailEn.trim();
  });
  return missing || null;
}

export function todayISODate(): string {
  return new Date().toISOString().slice(0, 10);
}

export const emptyAgentForm = (): AgentForm => ({
  name: '',
  strategyId: '',
  strategyName: '',
  description: '',
  avatar: '',
  isActive: true,
  symbol: '',
  ohlcv: '',
  version: '',
  riskLevel: '',
  productTags: '',
  winRate: '',
  sharpe: '',
  maxDrawdown: '',
  pnl30d: '',
  sevenDayReturn: '',
  fromBeginning: '',
  restoreDefaultProfile: false,
  strategyNameLang: emptyLangPack(),
  descriptionLang: emptyLangPack(),
  strategyNameLangDirty: false,
  descriptionLangDirty: false,
  i18nStale: false,
});

export const emptyCardForm = (): CardForm => ({
  strategyId: '',
  agentId: '',
  title: '',
  symbol: '',
  ohlcv: '1 hrs',
  side: 'long',
  entry: '',
  stopLoss: '',
  takeProfit: '',
  leverage: '10',
  confidence: '80',
  version: '1.0.0',
  riskLevel: 'medium',
  productTags: 'perp',
  // Form stores percent points; API uses decimal ratios (85 → 0.85).
  winRate: '50',
  sharpe: '1',
  maxDrawdown: '10',
  pnl30d: '0',
  sevenDayReturn: '0',
  fromBeginning: '0',
  reasoning: defaultReasoningForm(),
  cardValidHours: '24',
  source: 'XBIT',
});

export function numOrUndef(s: string): number | undefined {
  const t = s.trim();
  if (!t) return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
}

/** API ratio → form percent points (0.85 → "85"). */
export function ratioToPercentStr(ratio: number | null | undefined): string {
  if (ratio == null || !Number.isFinite(ratio)) return '';
  return String(Number((ratio * 100).toPrecision(12)));
}

/** Form percent points → API ratio (85 → 0.85). */
export function percentStrToRatio(s: string): number | undefined {
  const n = numOrUndef(s);
  if (n === undefined) return undefined;
  return n / 100;
}

export function agentFormFromAgent(a: StAgent): AgentForm {
  const d = defaultsOf(a);
  return {
    name: a.name || '',
    strategyId: a.strategyId || '',
    strategyName: a.strategyName || '',
    description: a.description || '',
    avatar: a.avatar || '',
    isActive: a.isActive,
    symbol: d.symbol || '',
    ohlcv: d.ohlcv || '',
    version: d.version || '',
    riskLevel: d.riskLevel || '',
    productTags: (d.productTags || []).join(','),
    winRate: ratioToPercentStr(d.winRate),
    sharpe: d.sharpe != null ? String(d.sharpe) : '',
    maxDrawdown: ratioToPercentStr(d.maxDrawdown),
    pnl30d: ratioToPercentStr(d.pnl30d),
    sevenDayReturn: ratioToPercentStr(d.sevenDayReturn),
    fromBeginning: ratioToPercentStr(d.fromBeginning),
    restoreDefaultProfile: false,
    strategyNameLang: packFromEntries(a.strategyNameLang, a.strategyName || ''),
    descriptionLang: packFromEntries(a.descriptionLang, a.description || ''),
    strategyNameLangDirty: false,
    descriptionLangDirty: false,
    i18nStale: false,
  };
}

/**
 * Prefill Send Signal from admin default profile, falling back to the
 * latest-signal overlay. Editing Agent profile writes defaultProfile and
 * does not clear the overlay, so overlay-first would ignore the edit.
 */
export function cardFormFromAgent(a: StAgent): CardForm {
  const base = emptyCardForm();
  const d = defaultsOf(a);
  base.agentId = a.agentId;
  base.strategyId = a.strategyId || '';
  base.title = a.strategyName || a.name || '';
  base.symbol = d.symbol || a.symbol || '';
  base.ohlcv = d.ohlcv || a.ohlcv || base.ohlcv;
  base.version = d.version || a.version || base.version;
  base.riskLevel = d.riskLevel || a.riskLevel || base.riskLevel;
  base.productTags = (d.productTags?.length ? d.productTags : a.productTags || ['perp']).join(',');
  base.winRate = ratioToPercentStr(d.winRate ?? a.winRate ?? 0.5) || '50';
  base.sharpe = String(d.sharpe ?? a.sharpe ?? 1);
  base.maxDrawdown = ratioToPercentStr(d.maxDrawdown ?? a.maxDrawdown ?? 0.1) || '10';
  base.pnl30d = ratioToPercentStr(d.pnl30d ?? a.pnl30d ?? 0) || '0';
  base.sevenDayReturn = ratioToPercentStr(d.sevenDayReturn ?? a.sevenDayReturn ?? 0) || '0';
  base.fromBeginning = ratioToPercentStr(d.fromBeginning ?? a.fromBeginning ?? 0) || '0';
  const reasoning = a.reasoning?.length ? a.reasoning : d.reasoning;
  base.reasoning = reasoningFormFromEntries(reasoning);
  return base;
}

export function equityCurveFromAgent(a: StAgent | null): StAgentEquityPoint[] {
  const d = a ? defaultsOf(a) : {};
  if (d.equityCurve?.length) return d.equityCurve;
  if (a?.equityCurve?.length) return a.equityCurve;
  return [{ date: todayISODate(), value: 1 }];
}
